import { Link } from 'react-router-dom';
import { Phone, MessageCircle, ArrowRight, Car, Plane, MapPin, Clock, Baby, Star, PhoneCall, CheckCircle2, CarTaxiFront } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import ChildSeatBand from '@/components/ChildSeatBand';
import HeroCarousel from '@/components/HeroCarousel';
import PageSEO from '@/components/PageSEO';
import { BUSINESS, IMAGES } from '@/lib/constants';
import { webPage, service } from '@/lib/schemas';

const SERVICES = [
  {
    key: 'local',
    icon: Car,
    title: 'Local Rides',
    bullets: [
      'Errands, appointments, & shopping',
      'Ferry connections (St. George ↔ Whitehall Terminal)',
      'Family rides & everyday transportation',
    ],
    href: '/local-rides-staten-island',
  },
  {
    key: 'airport',
    icon: Plane,
    title: 'Airport Trips',
    bullets: [
      'Newark Airport (EWR)',
      'JFK Airport',
      'LaGuardia (LGA)',
    ],
    href: '/airport-rides-staten-island',
  },
  {
    key: 'out-of-town',
    icon: MapPin,
    title: 'Out-of-Town Travel',
    bullets: [
      'Brooklyn & Manhattan',
      'Queens, Bronx, & Long Island',
      'New Jersey',
    ],
    href: '/request-a-ride',
  },
];

const HOW_IT_WORKS = [
  {
    icon: PhoneCall,
    title: 'Request Your Ride',
    desc: 'Call, WhatsApp, or submit a ride request online.',
  },
  {
    icon: CheckCircle2,
    title: 'We Confirm Details',
    desc: 'We confirm your pickup time, fare, and any special requests like child seats.',
  },
  {
    icon: CarTaxiFront,
    title: 'On Your Way',
    desc: 'Your driver arrives on time. Sit back, relax, and get where you need to go.',
  },
];

const HOME_SCHEMAS = [
  webPage('Island Ride — Staten Island Cab Service', 'Local Staten Island cab service for everyday rides, airport trips, and out-of-town travel.', ''),
  service('Local Rides in Staten Island', 'Local cab service', 'Errands, appointments, shopping, ferry connections, and family rides starting as low as $12.', '/local-rides-staten-island', { '@type': 'Offer', priceCurrency: 'USD', price: '12', description: 'Local rides starting as low as $12.' }),
  service('Airport Rides from Staten Island', 'Airport transportation', 'Rides from Staten Island to Newark (EWR), JFK, and LaGuardia (LGA). Tolls included.', '/airport-rides-staten-island'),
  service('Out-of-Town Travel', 'Out-of-town transportation', 'Trips to Brooklyn, Manhattan, New Jersey, and regional destinations by request.', '/request-a-ride'),
];

export default function Home() {
  return (
    <>
      <PageSEO
        title="Staten Island Cab Service — Local Rides, Airport Trips | Island Ride"
        description="Local Staten Island cab service for everyday rides, airport trips (Newark, JFK, LaGuardia), and out-of-town travel. Starting at $12. Pre-installed child seats available. Available 24/7."
        path=""
        schemas={HOME_SCHEMAS}
      />
      {/* HERO */}
      <section className="relative overflow-hidden" data-testid="home-hero">
        <div className="absolute inset-0 hero-overlay pointer-events-none" aria-hidden="true" />
        <div className="relative mx-auto w-full max-w-[1400px] px-4 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-14 items-center">
            <div>
              <Badge variant="secondary" className="mb-6 sm:mb-7 px-3 py-1 text-[11px] tracking-wide uppercase">Staten Island Cab Company</Badge>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-[3.75rem] font-semibold tracking-tight leading-[1.08]">
                Your <span className="text-primary">Staten Island</span> Cab Service
              </h1>
              <p className="mt-7 sm:mt-8 text-lg sm:text-xl text-foreground/85 max-w-xl font-medium">
                Local Rides <span className="text-primary/70 mx-2" aria-hidden="true">·</span> Airport trips <span className="text-primary/70 mx-2" aria-hidden="true">·</span> Out-of-Town travel
              </p>

              <div className="mt-9 sm:mt-10 flex flex-wrap gap-3">
                <Button asChild size="lg" data-testid="hero-call-button">
                  <a href={BUSINESS.phoneTel} aria-label={`Call ${BUSINESS.phone}`}>
                    <Phone className="h-4 w-4" /><span className="ml-1.5">{BUSINESS.phone}</span>
                  </a>
                </Button>
                <Button asChild size="lg" data-testid="hero-whatsapp-button">
                  <a href={BUSINESS.whatsappUrl} target="_blank" rel="noreferrer">
                    <MessageCircle className="h-4 w-4" /><span className="ml-1.5">WhatsApp</span>
                  </a>
                </Button>
                <Button asChild size="lg" data-testid="hero-request-button">
                  <Link to="/request-a-ride">Request a ride<ArrowRight className="h-4 w-4 ml-1.5" /></Link>
                </Button>
              </div>
            </div>

            <div className="block">
              <HeroCarousel />
            </div>
          </div>
        </div>
      </section>

      {/* PRICING / TRUST STRIP */}
      <section className="bg-primary text-primary-foreground" data-testid="pricing-trust-strip">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 py-7 sm:py-9">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-y-6 sm:gap-y-0">
            {/* Item 1 */}
            <div className="flex items-center justify-center gap-3 sm:border-r sm:border-white/20 px-4">
              <Star className="h-5 w-5 shrink-0 text-primary-foreground/80" />
              <span className="font-display text-base sm:text-lg font-semibold tracking-wide">
                Local Rides from <span className="text-xl sm:text-2xl">$12</span>
              </span>
            </div>
            {/* Item 2 */}
            <div className="flex items-center justify-center gap-3 sm:border-r sm:border-white/20 px-4">
              <Baby className="h-5 w-5 shrink-0 text-primary-foreground/80" />
              <span className="font-display text-base sm:text-lg font-semibold tracking-wide">
                Pre-Installed Child Seats
              </span>
            </div>
            {/* Item 3 */}
            <div className="flex items-center justify-center gap-3 px-4">
              <Car className="h-5 w-5 shrink-0 text-primary-foreground/80" />
              <span className="font-display text-base sm:text-lg font-semibold tracking-wide">
                Sedans · Minivans · SUVs
              </span>
            </div>
          </div>
          <p className="text-center text-[11px] sm:text-xs opacity-60 mt-4">
            Final fare depends on pickup, destination, timing, and ride details.
          </p>
        </div>
      </section>

      {/* SERVICE AREA BAND */}
      <section className="bg-secondary border-y" data-testid="service-area-band">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 py-10 sm:py-14">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-primary">Serving Staten Island and Beyond</h2>
              <p className="mt-3 text-muted-foreground max-w-xl">
                Island Ride serves Staten Island and beyond — local rides, airport trips, and out-of-town travel. We’re especially active around central Staten Island, with rides available across the island by request.
              </p>
              <p className="mt-3 text-muted-foreground max-w-xl">
                Need a ride? <a href={BUSINESS.phoneTel} className="text-primary font-medium pretty-link">Call</a> or <a href={BUSINESS.whatsappUrl} target="_blank" rel="noreferrer" className="text-primary font-medium pretty-link">WhatsApp us</a>.
              </p>
              <div className="mt-5 flex flex-wrap gap-2">
                <Button asChild data-testid="see-service-area-button">
                  <Link to="/staten-island-service-area">See full service area<ArrowRight className="h-4 w-4 ml-1.5" /></Link>
                </Button>
                <Button asChild variant="outline" data-testid="service-area-band-request-button">
                  <Link to="/request-a-ride">Request a ride<ArrowRight className="h-4 w-4 ml-1.5" /></Link>
                </Button>
              </div>
            </div>
            <div className="flex justify-center">
              <div
                className="w-full max-w-md rounded-2xl overflow-hidden bg-background border shadow-[var(--shadow-card)]"
                data-testid="service-area-map-wrap"
              >
                <img
                  src={IMAGES.serviceAreaMap}
                  alt="Staten Island service-area graphic for Island Ride showing an especially active area around central and eastern Staten Island, including New Dorp, Grant City, Dongan Hills, Oakwood, Richmondtown, Todt Hill, Arrochar, and Shore Acres, with rides available across Staten Island by request."
                  className="block w-full h-auto"
                  loading="lazy"
                  data-testid="service-area-map-img"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="section-pad" style={{ background: 'linear-gradient(to bottom, rgba(16,97,95,0.06), rgba(16,97,95,0))' }} data-testid="how-it-works">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6">
          {/* Centered header */}
          <div className="text-center max-w-2xl mx-auto">
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-primary">
              How It Works
            </h2>
          </div>

          {/* Steps + connecting line */}
          <div className="relative mt-12 sm:mt-16">
            {/* Horizontal connecting line — desktop/tablet only */}
            <div
              aria-hidden="true"
              className="hidden sm:block absolute left-[16.66%] right-[16.66%] top-[84px] h-px bg-gradient-to-r from-primary/0 via-primary/35 to-primary/0"
            />

            <ol className="relative grid grid-cols-1 sm:grid-cols-3 gap-y-14 sm:gap-y-0 sm:gap-x-6">
              {HOW_IT_WORKS.map((step, i) => (
                <li key={step.title} className="flex flex-col items-center text-center">
                  {/* Square icon pill */}
                  <div
                    className="relative w-20 h-20 rounded-2xl bg-accent/55 border border-primary/15 flex items-center justify-center shadow-[var(--shadow-soft)]"
                    data-testid={`how-step-${i + 1}-pill`}
                  >
                    <step.icon
                      className="h-8 w-8 text-primary"
                      strokeWidth={1.75}
                      aria-hidden="true"
                    />
                  </div>

                  {/* Anchor dot on the connecting line (desktop/tablet only) */}
                  <span
                    aria-hidden="true"
                    className="hidden sm:block w-2.5 h-2.5 rounded-full bg-primary -mt-[6px] ring-4 ring-background z-10"
                  />

                  {/* Step number */}
                  <p
                    className="mt-5 sm:mt-6 font-display text-sm font-semibold tracking-[0.22em] text-primary"
                    data-testid={`how-step-${i + 1}-number`}
                  >
                    {String(i + 1).padStart(2, '0')}
                  </p>

                  {/* Title */}
                  <h3
                    className="mt-2 font-display text-lg sm:text-xl font-bold text-foreground max-w-[16rem]"
                    data-testid={`how-step-${i + 1}-title`}
                  >
                    {step.title}
                  </h3>

                  {/* Description */}
                  <p className="mt-2 text-sm sm:text-base text-muted-foreground max-w-[18rem] leading-relaxed">
                    {step.desc}
                  </p>
                </li>
              ))}
            </ol>
          </div>

          {/* Bottom CTAs */}
          <div className="mt-12 sm:mt-14 flex flex-wrap justify-center gap-3">
            <Button asChild data-testid="how-it-works-call-button">
              <a href={BUSINESS.phoneTel}><Phone className="h-4 w-4" /><span className="ml-1.5">Call {BUSINESS.phone}</span></a>
            </Button>
            <Button asChild data-testid="how-it-works-whatsapp-button">
              <a href={BUSINESS.whatsappUrl} target="_blank" rel="noreferrer"><MessageCircle className="h-4 w-4" /><span className="ml-1.5">WhatsApp</span></a>
            </Button>
            <Button asChild data-testid="how-it-works-request-button">
              <Link to="/request-a-ride">Request a ride<ArrowRight className="h-4 w-4 ml-1.5" /></Link>
            </Button>
          </div>
        </div>
      </section>

      {/* SERVICES TILES — "Where You Going?" */}
      <section className="bg-secondary border-y" data-testid="where-you-going">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 py-10 sm:py-14">
          <div className="max-w-2xl">
            <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-primary">Where You Going?</h2>
            <p className="mt-2 text-muted-foreground">Three core services for everyday Staten Island transportation.</p>
          </div>
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
            {SERVICES.map((s) => (
              <Card
                key={s.key}
                className="p-5 transition-[box-shadow,transform] duration-200 hover:-translate-y-0.5 hover:shadow-[var(--shadow-soft)]"
                data-testid={`service-tile-${s.key}`}
              >
                <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-secondary text-primary">
                  <s.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-3 font-display text-lg font-semibold">{s.title}</h3>
                <ul className="mt-2 space-y-1.5">
                  {s.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-primary/50 shrink-0" aria-hidden="true" />
                      {b}
                    </li>
                  ))}
                </ul>
                <div className="mt-3">
                  <Button asChild variant="ghost" className="px-0 h-auto" data-testid={`service-tile-link-${s.key}`}>
                    <Link to={s.href}>Learn more<ArrowRight className="h-4 w-4 ml-1" /></Link>
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CHILD SEAT BAND */}
      <ChildSeatBand />

      {/* BOTTOM CTA STRIP */}
      <section className="bg-primary text-primary-foreground" data-testid="bottom-cta-strip">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 py-3.5 sm:py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-center gap-y-3 gap-x-5 text-center">
            <span className="font-display font-semibold text-sm sm:text-[15px]">Need a ride in Staten Island?</span>
            <div className="flex flex-wrap items-center justify-center gap-2">
              <Button asChild size="sm" variant="secondary" className="h-8 text-xs" data-testid="bottom-cta-call">
                <a href={BUSINESS.phoneTel}><Phone className="h-3.5 w-3.5" /><span className="ml-1.5">Call {BUSINESS.phone}</span></a>
              </Button>
              <Button asChild size="sm" variant="secondary" className="h-8 text-xs" data-testid="bottom-cta-whatsapp">
                <a href={BUSINESS.whatsappUrl} target="_blank" rel="noreferrer"><MessageCircle className="h-3.5 w-3.5" /><span className="ml-1.5">WhatsApp</span></a>
              </Button>
              <Button asChild size="sm" variant="secondary" className="h-8 text-xs" data-testid="bottom-cta-request">
                <Link to="/request-a-ride">Request a ride<ArrowRight className="h-3.5 w-3.5 ml-1" /></Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
